import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ApiKey, ModalData } from '../Types';
import { setModal } from '../Store/Actions/modalActions';
import useGlobals from './useGlobals';
import { apiKeyService } from '../Services/apiKeyService';
import { setListReloadType } from '../Store/Actions/environmentActions';
import { PaginationListType } from '../Types/PaginationParams';

const useApiKeys = () => {
    const { handleNavigate, userSettings, settings, i18n, demo, windowWidth, getBreakpoint, dispatch } = useGlobals();
    const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
    const searchInput = useRef<HTMLInputElement>(null);

    const handleEdit = useCallback(
        (identifier: string) => {
            handleNavigate(identifier);
        },
        [handleNavigate],
    );

    const deleteApiKey = useCallback(
        (identifier: string) => {
            if (demo) return;

            if (!userSettings.isLoggedIn) return;

            apiKeyService.deleteApiKey(identifier).then(() => {
                dispatch(setListReloadType(PaginationListType.ApiKeys));
            });
        },
        [demo, userSettings.isLoggedIn, dispatch],
    );

    const confirmDeleteApiKey = useCallback(
        async (apiKeyIdentifierToDelete: string) => {
            if (apiKeyIdentifierToDelete) {
                deleteApiKey(apiKeyIdentifierToDelete);
            }
        },
        [deleteApiKey],
    );

    //TRANSLATE
    const handleOpenConfirmDialog = useCallback(
        (apiKeyIdentifier: string) => {
            dispatch(
                setModal({
                    state: 'open',
                    title: 'Delete ApiKey',
                    content: 'Are you sure you want to delete this ApiKey?',
                    modalSize: 'small',
                    buttonLeftText: 'No',
                    buttonRightText: 'Yes',
                    closeOnAction: true,
                    showCloseButton: false,
                    buttonRightAction: () => confirmDeleteApiKey(apiKeyIdentifier),
                } as ModalData),
            );
        },
        [dispatch, confirmDeleteApiKey],
    );

    useEffect(() => {
        if (!userSettings.isLoggedIn) return;
        apiKeyService.getAllApiKeysPaginated().then((response) => {
            setApiKeys(response);
        });
    }, [userSettings.isLoggedIn, demo]);

    const ApiKeysActions = useMemo(
        () => ({
            handleEdit,
            deleteApiKey,
            handleOpenConfirmDialog,
            confirmDeleteApiKey,
        }),
        [handleEdit, deleteApiKey, handleOpenConfirmDialog, confirmDeleteApiKey],
    );

    return {
        apiKeys,
        ApiKeysActions,
        settings,
        demo,
        i18n,
        getBreakpoint,
        windowWidth,
        searchInput,
        handleNavigate,
    };
};

export default useApiKeys;
